import type { ApplicationVulnerabilities, EnvironmentVulnerabilities, Vulnerability } from '../../../../store/scan-api'
import type { StatusPopoverType } from '../../status-popover'

type VulnerabilitySeverity = Vulnerability['severity']
export type VulnerabilityCounts = Record<Lowercase<VulnerabilitySeverity>, number>

/** Severities that are surfaced on the environment card status badge. */
export const VISIBLE_VULNERABILITY_SEVERITIES: ReadonlyArray<Lowercase<VulnerabilitySeverity>> = ['critical', 'high']

const emptyVulnerabilityCounts: Readonly<VulnerabilityCounts> = {
  critical: 0,
  high: 0,
  medium: 0,
  low: 0,
  unknown: 0,
}

/** Adds a raw per-severity summary (case-insensitive keys) into an accumulating count. */
const addVulnerabilitiesToTotal = (
  total: VulnerabilityCounts,
  vulnerabilities: Record<string, number> | undefined
): VulnerabilityCounts => {
  for (const [severity, count] of Object.entries(vulnerabilities ?? {})) {
    total[severity.toLowerCase() as Lowercase<VulnerabilitySeverity>] += count
  }
  return total
}

/** Aggregates the per-severity vulnerability counts across every component and job in an environment. */
export const summarizeEnvironmentVulnerabilities = (envScan?: EnvironmentVulnerabilities): VulnerabilityCounts => {
  const scans = [...Object.values(envScan?.components ?? {}), ...Object.values(envScan?.jobs ?? {})]

  return scans.reduce<VulnerabilityCounts>(
    (total, scan) => addVulnerabilitiesToTotal(total, scan.vulnerabilitySummary),
    { ...emptyVulnerabilityCounts }
  )
}

/** Aggregates the per-severity vulnerability counts across every environment in an application. */
export const summarizeApplicationVulnerabilities = (appScan?: ApplicationVulnerabilities): VulnerabilityCounts =>
  (appScan ?? []).reduce<VulnerabilityCounts>(
    (total, envScan) => addVulnerabilitiesToTotal(total, summarizeEnvironmentVulnerabilities(envScan)),
    { ...emptyVulnerabilityCounts }
  )

export const hasAnyVulnerabilities = (summary: VulnerabilityCounts): boolean =>
  Object.values(summary).some((value) => value > 0)

export const hasVisibleVulnerabilities = (
  summary: VulnerabilityCounts,
  visibleSeverities: ReadonlyArray<Lowercase<VulnerabilitySeverity>>
): boolean => visibleSeverities.some((key) => summary[key] > 0)

export const getStatusPopoverType = (summary: VulnerabilityCounts): StatusPopoverType => {
  if (summary.critical > 0) return 'danger'
  if (summary.high > 0 || summary.unknown > 0) return 'warning'
  if (summary.medium > 0 || summary.low > 0) return 'success'
  return 'none'
}
